/**
 * Repair Catalog API Composable
 * Fetches repair categories, services, pricing and stores from backend
 */
import { apiGet } from '~/utils/api'

// ==================== Types ====================

export interface RepairServiceItem {
  id: string
  name: string
  slug: string
  description: string
  nameTranslations: Record<string, string>
  descriptionTranslations: Record<string, string>
  basePrice: number
  seriesPrice: number | null
  productPrice: number | null
  durationMins: number
  warrantyMonths: number
  isOriginalParts: boolean
}

export interface RepairCategoryItem {
  id: string
  name: string
  slug: string
  icon: string
  description: string
  nameTranslations: Record<string, string>
  descriptionTranslations: Record<string, string>
  sortOrder: number
  services: RepairServiceItem[]
}

export interface RepairStoreItem {
  id: string
  name: string
  slug: string
  address: string
  city: string
  postalCode: string
  phone: string
  email: string
  latitude: number
  longitude: number
  businessHours: Record<string, string>
}

// ==================== Composable ====================

export const useRepairAPI = () => {
  const loading = ref(false)
  const error = ref<string | null>(null)

  /**
   * 获取维修类别（含服务列表）
   * 传入 productId 时返回该产品的专属定价
   */
  async function fetchCategories(productId?: string): Promise<RepairCategoryItem[]> {
    loading.value = true
    error.value = null
    try {
      const params = productId ? `?productId=${productId}` : ''
      return await apiGet<RepairCategoryItem[]>(`/api/app/v1/repair/categories${params}`)
    } catch (e: any) {
      error.value = e.message || 'Failed to load repair categories'
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 获取启用门店列表
   */
  async function fetchStores(): Promise<RepairStoreItem[]> {
    loading.value = true
    error.value = null
    try {
      return await apiGet<RepairStoreItem[]>('/api/app/v1/repair/stores')
    } catch (e: any) {
      error.value = e.message || 'Failed to load repair stores'
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 获取服务的实际价格（型号专属价 > 设备系列价 > 服务基础价）
   */
  function getEffectivePrice(service: RepairServiceItem): number {
    return service.productPrice ?? service.seriesPrice ?? service.basePrice
  }

  return { loading, error, fetchCategories, fetchStores, getEffectivePrice }
}
